import { useEffect, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { telephonyApi, type AgentCallHistoryEntry, type CallHistoryPage } from './api'

/**
 * HISTORIQUE DE L'AGENT — recherche et pagination **SERVEUR**, cache **INTER-PORTES**.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * LA LEÇON QUI DICTE CE FICHIER
 * ══════════════════════════════════════════════════════════════════════════
 *
 * La v1 chargeait TOUT (`SimpleDialerPage:526`, `getAll({})` sans limite, deux
 * fois par fin d'appel) puis filtrait en JavaScript (`:221-230`). C'est la
 * cause de lourdeur relevée par l'utilisateur. Ici : la recherche part au
 * serveur, la pagination aussi, et « Charger plus » demande une page — jamais
 * la liste entière.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * POURQUOI CE HOOK EST PASSÉ SUR REACT QUERY (SCENE-2B, point 3)
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Il tenait ses pages dans un `useState`. Or le panneau qui l'utilise n'est
 * rendu QUE sur la porte manuelle au repos : chaque bascule Manuel → Campagne
 * → Manuel le démontait, jetait ses pages, et **rechargeait tout à zéro**. Un
 * agent qui vérifie sa campagne puis revient au composeur payait donc une
 * requête (et un écran vide) pour une liste qui n'avait pas bougé d'un iota.
 *
 * Désormais les pages vivent dans le cache React Query, hors du cycle de vie du
 * composant. Deux conséquences, et ce sont exactement les deux exigences :
 *
 *  - **le switch de porte ne refetch PAS** — `staleTime: Infinity` : rien n'est
 *    jamais considéré périmé de soi-même, donc un remontage se sert dans le
 *    cache, sans requête ni scintillement ;
 *  - **la liste se rafraîchit UNIQUEMENT sur l'événement de FIN D'APPEL** —
 *    l'invalidation est posée par `useCallEndRefresh`, monté dans le provider,
 *    donc vivant PENDANT l'appel, là où ce hook-ci est démonté. C'est le seul
 *    moment où l'historique de l'agent change de son fait.
 *
 * Restent les deux propriétés acquises en #45, portées maintenant par la clé de
 * requête : la recherche est **anti-frappe** (300 ms de repos) et **anti-course**
 * (une réponse en retard appartient à une AUTRE clé, elle ne peut donc plus
 * recouvrir l'affichage courant — la garde de séquence manuelle disparaît avec
 * la cause qu'elle traitait).
 */

/** Taille d'une page. Le serveur plafonne de toute façon (contrat côté API). */
export const CALL_HISTORY_PAGE_SIZE = 15

/** Repos clavier avant d'interroger le serveur. */
export const SEARCH_DEBOUNCE_MS = 300

/** Racine de clé — partagée avec l'invalidation de fin d'appel. */
export const CALL_HISTORY_QUERY_KEY = ['telephony', 'agent-history'] as const

export interface CallHistorySearch {
  entries: AgentCallHistoryEntry[]
  search: string
  setSearch: (value: string) => void
  isLoading: boolean
  isError: boolean
  /** Une page suivante existe (bouton « Charger plus »). */
  hasMore: boolean
  isLoadingMore: boolean
  loadMore: () => void
}

export function useCallHistorySearch(params: { enabled: boolean }): CallHistorySearch {
  const { enabled } = params

  const [search, setSearch] = useState('')
  /**
   * Terme RÉELLEMENT interrogé. Il n'entre dans la clé de requête qu'après le
   * repos clavier : sans lui, un numéro de dix chiffres créerait dix entrées de
   * cache et dix requêtes.
   */
  const [term, setTerm] = useState('')

  useEffect(() => {
    const trimmed = search.trim()
    // Effacer la recherche revient à l'affichage par défaut : c'est immédiat
    // (et gratuit — cette clé-là est déjà en cache).
    const delay = trimmed ? SEARCH_DEBOUNCE_MS : 0
    const timer = setTimeout(() => setTerm(trimmed), delay)
    return () => clearTimeout(timer)
  }, [search])

  const query = useInfiniteQuery<CallHistoryPage, Error, { pages: CallHistoryPage[] }, readonly unknown[], string | null>({
    queryKey: [...CALL_HISTORY_QUERY_KEY, term],
    queryFn: ({ pageParam }) =>
      telephonyApi.listHistory({
        search: term || undefined,
        limit: CALL_HISTORY_PAGE_SIZE,
        cursor: pageParam ?? null,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled,
    // ⚠️ LE CŒUR DU POINT 3 : jamais périmé de soi-même. Aucun remontage, aucun
    // retour de focus, aucune reconnexion ne déclenche de requête — seule
    // l'invalidation explicite de fin d'appel le fait.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  const entries = (query.data?.pages ?? []).flatMap((page) => page.entries)

  return {
    entries,
    search,
    setSearch,
    // PREMIÈRE lecture seulement : un rafraîchissement de fin d'appel ne doit
    // pas vider l'écran sous les yeux de l'agent.
    isLoading: enabled && query.isPending,
    isError: query.isError,
    hasMore: Boolean(query.hasNextPage),
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (!query.hasNextPage || query.isFetchingNextPage) return
      void query.fetchNextPage()
    },
  }
}
